import React, { useState } from "react";
import RequestService from "../../services/requestService";
import RequestRecurringService from "../../services/requestRecurringService";
import { toast } from "react-toastify";
import { showHolidayConflictAlert } from "../../components/utils/recurring-holiday-alert";
import { showLoading, hideLoading, showSuccess, showError } from "../../components/utils/swal-helpers";

// ── Helper: แปลง ISO date ("2026-08-12") เป็น DD/MM/YYYY ให้ตรงกับ format ที่ dates ใช้ ──
const isoToThaiDate = (iso) => {
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

// ── Helper: parse "15/8/2026" (d/m/yyyy) → Date ──
const parseDateTH = (str) => {
  if (!str) return null;
  const [d, m, y] = String(str).split("/").map(Number);
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
};

// ── Helper: เช็คว่าเป็นวันเสาร์-อาทิตย์ ──
const isWeekend = (dateStr) => {
  const d = parseDateTH(dateStr);
  if (!d || isNaN(d.getTime())) return false;
  const day = d.getDay();
  return day === 0 || day === 6;
};

// ── Sub-component ─────────────────────────────────────────────────────────────
const DateListItem = ({ date }) => (
  <div className="rp-list-item">
    <i className="mdi mdi-package-variant rp-list-icon" />
    <span>{date}</span>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const RecurringPreview = ({ dates = [], unavailableDates = [], buildMainPayload, recurringPayload, onCancel, onBack, onSaveSuccess, onEditConflict }) => {
  const [saving, setSaving] = useState(false);

  const callCreate = async () => {
    if (!buildMainPayload) {
      return;
    }
    const payload = buildMainPayload();

    // ═══════════════════════════════════════════════════════════
    // 🔌 API CALL: POST /requests
    // สร้างคำขอจริงลง DB (เรียกตอนกด Save ในหน้า Preview นี้)
    // ═══════════════════════════════════════════════════════════
    const res = await RequestService.CreateRequest(payload);

    if (res?.data?.is_success) {
      hideLoading();
      await showSuccess({ title: "สร้างใบคำขอสำเร็จ" });
      onSaveSuccess?.();
    } else {
      hideLoading();
      showError();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // ── วันที่ชน weekend/holiday จาก backend (unavailable_dates) ส่งมาทาง prop ──
      const conflicts = [...unavailableDates]
        .sort((a, b) => parseDateTH(a) - parseDateTH(b));

      if (conflicts.length > 0) {
        const action = await showHolidayConflictAlert(conflicts);
        if (action === "cancel") return;
        if (action === "edit") { onEditConflict?.(conflicts); return; }
        if (action === "save") {
          if (buildMainPayload) {
            showLoading();
            await callCreate();
          } else {
            onSaveSuccess?.();
          }
          return;
        }
      }

      if (buildMainPayload) {
        showLoading();
        await callCreate();
      } else {
        onSaveSuccess?.();
      }
    } catch (err) {
      hideLoading();
      showError();
    } finally {
      setSaving(false);
    }
};

  return (
    <div className="page-wrapper">
      {/* Top Bar */}
      <div className="rp-top-bar">
        <div>
          <div className="rp-page-title">คำขอที่จะเกิดขึ้นทั้งหมด</div>
          <div className="page-subtitle">รายการคำขอทั้งหมดที่จะเกิดขึ้นในซีรีย์นี้</div>
        </div>
        <button type="button" className="rp-cancel-link" onClick={onCancel}>
          <i className="mdi mdi-rotate-left" /> Cancel recurring setup
        </button>
      </div>

      {/* Card */}
      <div className="rp-card">
        <div className="rp-card-header">
          <span className="rp-card-title">คำขอทั้งหมด</span>
          <span className="rp-card-count">มีทั้งหมด {dates.length} รายการ</span>
        </div>
        <div className="rp-list">
          {dates.length === 0
            ? <div className="rp-empty">ไม่มีรายการ</div>
            : dates.map((date, index) => <DateListItem key={index} date={date} />)
          }
        </div>
      </div>

      {/* Footer */}
      <div className="form-footer">
        <div />
        <div className="rp-footer-actions">
          <button
            type="button"
            className="btn btn-secondary btn-back"
            onClick={onBack}
            disabled={saving}
          >
            <i className="ri-arrow-left-line" /> Back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={dates.length === 0 || saving}
          >
            {saving ? "กำลังบันทึก..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecurringPreview;